import {
  Box,
  Button,
  Flex,
  FormControl,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { Currency, OriginalProfitAndLossByMonthTable, ProjectWithRdsData } from "../../contexts/types";
import { CustomNumberInput } from "./CustomNumberInput";
import { isPropsEqual } from "../../utils/utils";

/**
 * 新製品製造シミュレーションの「生産工場費用設定」セクションを表示するコンポーネントのProps
 */
interface Props {
  /** GoogleSpread連携時の無効値エラー情報 */
  lcSyncError: { invalidValueErrors: string[] };
  /** 生産工場労務費単価 */
  scenarioProductionFactoryLaborUnitPrice: number | null;
  /** 生産工場労務費単価の更新関数 */
  setScenarioProductionFactoryLaborUnitPrice: (value: number | null) => void;
  /** 生産工場経費単価 */
  scenarioProductionFactoryExpenseUnitPrice: number | null;
  /** 生産工場経費単価の更新関数 */
  setScenarioProductionFactoryExpenseUnitPrice: (value: number | null) => void;
  /** ベースアップ率 */
  scenarioBaseLaborCostIncreaseRate: number | null;
  /** ベースアップ率の更新関数 */
  setScenarioBaseLaborCostIncreaseRate: (value: number | null) => void;
  /** 通貨マスタ */
  currencyData: Currency[];
  /** プロジェクト情報 */
  projectData: ProjectWithRdsData | null | undefined;
  /** 当初計画の月次損益テーブル */
  originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable | undefined;
  /** 月別生産工場労務費 */
  scenarioLaborCostByMonth: number[];
  /** 月別生産工場労務費の更新関数 */
  setScenarioLaborCostByMonth: React.Dispatch<React.SetStateAction<number[]>>;
  /** 月別償却費以外の工場経費 */
  scenarioOverseasFactoryExpensesByMonth: number[];
  /** 月別償却費以外の工場経費の更新関数 */
  setScenarioOverseasFactoryExpensesByMonth: React.Dispatch<React.SetStateAction<number[]>>;
  /** 労務費・工場経費の自動計算ボタン押下時の処理 */
  handleSimulationAutoCalculateLaborCost: () => Promise<void>;
  /** 労務費・工場経費の自動計算中フラグ */
  isLaborCostCalculating: boolean;
}

/**
 * 新製品製造シミュレーションの「生産工場費用設定」セクションを表示するコンポーネント
 * SimulationNewProduct.tsx から切り出した対応B（コンポーネント分割 + React.memo化）
 */
const ProductionFactoryCostSection: React.FC<Props> = memo((props) => {
  const {
    lcSyncError,
    scenarioProductionFactoryLaborUnitPrice,
    setScenarioProductionFactoryLaborUnitPrice,
    scenarioProductionFactoryExpenseUnitPrice,
    setScenarioProductionFactoryExpenseUnitPrice,
    scenarioBaseLaborCostIncreaseRate,
    setScenarioBaseLaborCostIncreaseRate,
    currencyData,
    projectData,
    originalProfitAndLossByMonth,
    scenarioLaborCostByMonth,
    setScenarioLaborCostByMonth,
    scenarioOverseasFactoryExpensesByMonth,
    setScenarioOverseasFactoryExpensesByMonth,
    handleSimulationAutoCalculateLaborCost,
    isLaborCostCalculating,
  } = props;

  const { t } = useTranslation();

  // console.info(`★レンダリング - 生産工場費用設定`); // 再描画確認用

  return (
    <Box mt={6} p={4} border="1px solid #ccc" borderRadius="md" shadow="sm">
      <Text mb={4} fontSize="lg" fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.heading.laborCostSettings')}</Text>
      {/* GoogleSpread連携時の無効値エラーメッセージ */}
      {lcSyncError.invalidValueErrors.length > 0 && (
        <Box mb={2}>
          {lcSyncError.invalidValueErrors.map((error, index) => (
            <Text key={`lc-sync-error-${index}`} color="orange.500" fontSize="sm">{error}</Text>
          ))}
        </Box>
      )}
      {/* 費用画面で設定した単価・ベースアップ率を初期表示し、シミュレーション的に変更可能にする */}
      <Box mb={4}>
        <FormControl>
          <Flex align="center" mb={2}>
            <Text fontWeight="bold" mr={4} whiteSpace="nowrap" minW="200px">
              {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryLaborUnitPrice')}
            </Text>
            <CustomNumberInput
              value={scenarioProductionFactoryLaborUnitPrice ?? null}
              onChange={(value) => setScenarioProductionFactoryLaborUnitPrice(value)}
              min={0}
              max={10000}
              w={150}
              decimalPlaces={4}
              size="sm"
            />
            <Text ml={2} whiteSpace="nowrap">{currencyData.find(c => c.currencyId === projectData?.productionLaborCostCurrencyId)?.currencyName || ""} / min</Text>
          </Flex>
          <Flex align="center" mb={2}>
            <Text fontWeight="bold" mr={4} whiteSpace="nowrap" minW="200px">
              {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryExpenseUnitPrice')}
            </Text>
            <CustomNumberInput
              value={scenarioProductionFactoryExpenseUnitPrice ?? null}
              onChange={(value) => setScenarioProductionFactoryExpenseUnitPrice(value)}
              min={0}
              max={10000}
              w={150}
              decimalPlaces={4}
              size="sm"
            />
            <Text ml={2} whiteSpace="nowrap">{currencyData.find(c => c.currencyId === projectData?.overseasFactoryExpensesCurrencyId)?.currencyName || ""} / min</Text>
          </Flex>
          <Flex align="center">
            <Text fontWeight="bold" mr={4} whiteSpace="nowrap" minW="200px">
              {t('features.InvestmentScreening.ProfitAndLoss.label.baseLaborCostIncreaseRate')}
            </Text>
            <CustomNumberInput
              value={scenarioBaseLaborCostIncreaseRate ?? null}
              onChange={(value) => setScenarioBaseLaborCostIncreaseRate(value)}
              min={0}
              max={100}
              w={150}
              decimalPlaces={2}
              size="sm"
            />
            <Text ml={2} whiteSpace="nowrap">%</Text>
          </Flex>
        </FormControl>
      </Box>
      <HStack mb={4}>
        <Button
          colorScheme="blue"
          onClick={handleSimulationAutoCalculateLaborCost}
          isLoading={isLaborCostCalculating}
        >
          {t('features.InvestmentScreening.SimulationNewProduct.button.autoCalc')}
        </Button>
      </HStack>
      <Box
        className="simulation-scrollable-container"
        w="100%"
        minW={0}
        overflowX="auto"
      >
        <Grid
          templateColumns={`210px repeat(${(originalProfitAndLossByMonth?.labels ?? []).length}, 150px)`}
          gap={4}
        >
          {/* ヘッダー */}
          <GridItem>
            <Text fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.label.item')}</Text>
          </GridItem>
          {(originalProfitAndLossByMonth?.labels ?? []).map((label, idx) => {
            const [year, month] = label.split("/");
            return (
              <Text textAlign="center" fontWeight="bold" key={`labor-header-${idx}`}>
                {t('common.dateFormat.yearMonth', { year, month })}
              </Text>
            );
          })}

          {/* 生産工場労務費 */}
          <GridItem>
            <Text>
              {t('features.InvestmentScreening.SimulationNewProduct.label.productionLaborCost')}
              {(() => {
                const unit = currencyData.find(c => c.currencyId === projectData?.productionLaborCostCurrencyId)?.currencyName;
                return unit ? ` (${unit})` : "";
              })()}
            </Text>
          </GridItem>
          {(originalProfitAndLossByMonth?.labels ?? []).map((_, idx) => (
            <GridItem key={`labor-cost-${idx}`}>
              <CustomNumberInput
                value={scenarioLaborCostByMonth[idx] ?? 0}
                onChange={(value) => {
                  setScenarioLaborCostByMonth(prev => {
                    const updated = [...prev];
                    while (updated.length <= idx) updated.push(0);
                    updated[idx] = value ?? 0;
                    return updated;
                  });
                }}
                max={Number.MAX_VALUE}
                decimalPlaces={2}
                size="sm"
              />
            </GridItem>
          ))}

          {/* 償却費以外の工場経費 */}
          <GridItem>
            <Text>
              {t('features.InvestmentScreening.SimulationNewProduct.label.overseasFactoryExpenses')}
              {(() => {
                const unit = currencyData.find(c => c.currencyId === projectData?.overseasFactoryExpensesCurrencyId)?.currencyName;
                return unit ? ` (${unit})` : "";
              })()}
            </Text>
          </GridItem>
          {(originalProfitAndLossByMonth?.labels ?? []).map((_, idx) => (
            <GridItem key={`overseas-expense-${idx}`}>
              <CustomNumberInput
                value={scenarioOverseasFactoryExpensesByMonth[idx] ?? 0}
                onChange={(value) => {
                  setScenarioOverseasFactoryExpensesByMonth(prev => {
                    const updated = [...prev];
                    while (updated.length <= idx) updated.push(0);
                    updated[idx] = value ?? 0;
                    return updated;
                  });
                }}
                max={Number.MAX_VALUE}
                decimalPlaces={2}
                size="sm"
              />
            </GridItem>
          ))}
        </Grid>
      </Box>
    </Box>
  );
});

ProductionFactoryCostSection.displayName = "ProductionFactoryCostSection";

export default ProductionFactoryCostSection;
